import { z } from 'zod';

/** Allowed ranges from design/system (Responsible play): time 15 min – 6 h, tokens 100 – 10,000 per day. */
export const LIMIT_BOUNDS = {
  minutesPerDay: { min: 15, max: 360 },
  tokensPerDay: { min: 100, max: 10_000 },
} as const;

/** PUT /api/limits. `null` = no limit. Lowering applies at once; raising/removing applies after 24 h. */
export const zLimits = z.object({
  tokensPerDay: z
    .number()
    .int()
    .min(LIMIT_BOUNDS.tokensPerDay.min)
    .max(LIMIT_BOUNDS.tokensPerDay.max)
    .nullable(),
  minutesPerDay: z
    .number()
    .int()
    .min(LIMIT_BOUNDS.minutesPerDay.min)
    .max(LIMIT_BOUNDS.minutesPerDay.max)
    .nullable(),
});
export type LimitsInput = z.infer<typeof zLimits>;

/** POST /api/limits/break. A break cannot be cancelled or shortened. */
export const BREAK_PERIODS = ['24h', '7d', '30d'] as const;
export const zBreak = z.object({ period: z.enum(BREAK_PERIODS) });

/**
 * "Today" for limits and daily bonus is the UTC calendar day (server-wide, not per user time zone).
 * `resetsAt` tells the client when the counters reset.
 */
export interface LimitsState {
  tokensPerDay: number | null;
  minutesPerDay: number | null;
  tokensUsedToday: number;
  minutesUsedToday: number;
  breakUntil: string | null;
  /** increasing a limit only takes effect after this time (cool-off) */
  pendingIncreaseAt: string | null;
  /** the values that will apply at `pendingIncreaseAt` */
  pendingIncrease: { tokensPerDay?: number | null; minutesPerDay?: number | null } | null;
  resetsAt: string;
}
